import Location from 'app/campout/Location';
import GameCamera from 'app/campout/GameCamera';
import GameHud from 'app/ui/GameHud';
import Gesture from 'app/ui/Gesture';
import HintsController from '../controller/HintsController';
import OnboardingController from '../controller/OnboardingController';
import { TIME_NIGHT } from 'app/constants/AppConstants';
import { getGA } from 'analytics/GoogleAnalytics';

const TRACK_TIME = 60 * 1000; // [miliseconds]

export default class Campout extends Phaser.State {
  init() {
    const saves = this.game.controller.saves;
    // saves.deleteSave('save01');
    saves.setSaveID('save01');
    if (saves.getSaves().length === 0) {
      saves.save({ data: {} });
    }

    //
    this.scrollsNumber = 0;

    // time tracking
    this.timeTotal = 0;
    this.timeDay = 0;
    this.timeSector1 = 0;
    this.timeSector2 = 0;
    this.timeSector3 = 0;
    this.game.time.events.add(TRACK_TIME, this.trackTime, this);
  }

  /* eslint-disable max-statements */
  create() {
    this.locationData = this.game.cache.getJSON('location_data');
    this.savedData = this.game.controller.saves.load('save01');

    this.container = this.game.add.group();

    //
    this.location = new Location(
      this.game,
      this.locationData,
      this.locationData.width,
      this.savedData,
      this
    );
    this.container.addChild(this.location);
    this.location.onShowMacroView.add(this.handleShowMacroView, this);

    // add GameCamera
    this.camera = new GameCamera(this.game, this.location, this);
    this.camera.setPosition(this.savedData.start_point || this.locationData.start_point);
    this.camera.onPosition.add(this.handleCameraOnPosition, this);
    this.navPointId = 0;
    this.calculateNavPoints();
    this.location.setCamera(this.camera); // add camera for location

    //

    this.location.enableControls();

    // add hints controller
    this.hintsController = new HintsController(
      this.game,
      this.game.cache.getJSON('hints_data'),
      this.locationData,
      this.location,
      this
    );

    // keyboard control
    // this.game.input.keyboard.removeKeyCapture(Phaser.Keyboard.LEFT);
    // this.game.input.keyboard.removeKeyCapture(Phaser.Keyboard.RIGHT);

    //
    this.gameHud = new GameHud(this.game, this);
    this.container.addChild(this.gameHud);
    this.gameHud.onToggleTime.add(this.handleToggleTime, this);
    this.gameHud.onShowHint.add(this.handleShowHint, this);
    this.gameHud.onScrollLeft.add(this.handleScrollLeft, this);
    this.gameHud.onScrollRight.add(this.handleScrollRight, this);
    this.setArrowsVisibility();

    if (this.savedData.time === TIME_NIGHT) {
      this.gameHud.btnToggleTime.setNight();
    }

    // onboarding controller
    if (!this.game.onboarding) {
      this.game.onboarding = new OnboardingController(this.game, this);
      this.game.onboarding.start();
    }

    //
    this.game.scale.onSizeChange.add(this.handleResize, this);
  }
  /* eslint-enable */

  handleResize() {
    this.calculateNavPoints();
    this.setCurrentNavPoint();
  }

  // scrolling scene
  handleScrollLeft() {
    this.setCurrentNavPoint();
    this.lockLocation();
    // this.gameHud.hideScrollArrows();
    if (this.navPointId > 0) {
      this.navPointId--;
    }
    this.camera.destX = this.navPoints[this.navPointId];
    this.gameHud.showScrollArrows(this.navPointId > 0, this.navPointId < this.navPoints.length - 1);
  }

  handleScrollRight() {
    this.setCurrentNavPoint();
    this.lockLocation();
    // this.gameHud.hideScrollArrows();
    if (this.navPointId < this.navPoints.length - 1) {
      this.navPointId++;
    }
    this.camera.destX = this.navPoints[this.navPointId];
    this.gameHud.showScrollArrows(this.navPointId > 0, this.navPointId < this.navPoints.length - 1);
  }

  setArrowsVisibility() {
    if (!this.gameHud.arrowsLocked) {
      this.setCurrentNavPoint();
      // this.gameHud.hideScrollArrows();
      const cx = this.camera.currentX;
      this.gameHud.showScrollArrows(
        cx >= this.navPoints[0] + 5,
        cx <= this.navPoints[this.navPoints.length - 1] - 5
      );
    }
  }

  handleCameraOnPosition() {
    this.setArrowsVisibility();
    this.unlockLocation();
  }

  //
  lockLocation() {
    if (!this.locationOverlay) {
      this.locationOverlay = this.game.make.image(0, 0, 'empty');
      this.locationOverlay.width = this.game.defaultWidth;
      this.locationOverlay.height = this.game.defaultHeight;
      this.locationOverlay.inputEnabled = true;
      this.locationOverlay.input.useHandCursor = false;

      const ind = this.container.getChildIndex(this.location) + 1;
      this.container.addChildAt(this.locationOverlay, ind);
    }

    this.locationOverlay.visible = true;
  }

  unlockLocation() {
    if (this.locationOverlay) {
      this.locationOverlay.visible = false;
    }
  }

  //
  handleToggleTime(dayTime) {
    this.location.setDayTime(dayTime);
  }

  //
  calculateNavPoints() {
    const x0 = this.game.width * 0.5;
    const x1 = this.locationData.width - this.game.width * 0.5;
    const dx = x1 - x0;
    this.navPoints = [];

    if (dx < this.game.width) {
      // distance between edges smaller than screen width
      this.navPoints.push(x0);
      this.navPoints.push(x1);
    } else {
      //
      const screens = Math.round(dx / this.game.width) + 1;
      const width = dx / screens;
      for (let i = 0; i < screens + 1; i++) {
        this.navPoints[i] = x0 + width * i;
      }
    }
  }

  setCurrentNavPoint() {
    const cx = this.camera.currentX;
    const x0 = this.game.width * 0.5;
    const x1 = this.locationData.width - this.game.width * 0.5;

    if (cx < x0) {
      this.navPointId = 0;
    } else if (cx > x1) {
      this.navPointId = this.navPoints.length - 1;
    } else {
      const width = this.navPoints[1] - this.navPoints[0];

      for (let i = 0; i < this.navPoints.length; i++) {
        const dx = Math.abs(this.navPoints[i] - cx);
        if (dx < width * 0.5) {
          this.navPointId = i;
          break;
        }
      }
    }
  }

  handleShowMacroView({ x, y, macroView, furniture, animInd }) {
    this.gameHud.showMacroView({ x, y, macroView, furniture, animInd });
  }

  handleShowHint() {
    if (this.loopHintId) {
      // loop hint
      this.hintsController.displayHint(null, null, this.loopHintId);
      this.removeLoopHint();
    } else {
      // normal hint
      this.hintsController.showHint();
    }
  }

  setLoopHint(hintId) {
    this.loopHintId = hintId;
    this.gameHud.btnHelp.growUp();
  }

  removeLoopHint() {
    this.loopHintId = null;
  }

  showGesture(x, y) {
    const gesture = new Gesture(this.game, x, y);
    this.location.foreground.addChild(gesture);
    return gesture;
  }

  interacted(object) {
    if (!this.game.onboarding.completed) {
      this.game.onboarding.interacted(object);
    }
  }

  scrolled() {
    if (!this.game.onboarding.completed) {
      this.game.onboarding.scrolled();
    } else {
      this.scrollsNumber++;
    }
  }

  //
  update() {
    const dt = Math.min(0.1, this.game.time.elapsedMS / 1000); // delta time in seconds

    // keyboard controls
    if (this.location.controlsEnabled) {
      if (this.game.input.keyboard.isDown(Phaser.Keyboard.LEFT)) {
        this.camera.destX -= 10;
      }
      if (this.game.input.keyboard.isDown(Phaser.Keyboard.RIGHT)) {
        this.camera.destX += 10;
      }
    }

    //
    this.location.step(dt);
    this.camera.step(dt);

    // drag hint
    if (!this.gameHud.dragHint && this.scrollsNumber < 2) {
      if (this.camera.stopTime > 20) {
        this.gameHud.showDragHint();
      }
    }

    // stats
    this.timeTotal += dt;
    this.timeDay += this.location.isDay() ? dt : 0;
    if (this.camera.currentX < 1800) {
      this.timeSector1 += dt;
    } else if (this.camera.currentX > 3500) {
      this.timeSector3 += dt;
    } else {
      this.timeSector2 += dt;
    }
  }

  trackTime() {
    this.game.time.events.add(TRACK_TIME, this.trackTime, this);

    const total = Math.round(this.timeTotal / (TRACK_TIME / 1000));
    const dayTime = Math.round((100 * this.timeDay) / this.timeTotal);
    const sector1 = Math.round((100 * this.timeSector1) / this.timeTotal);
    const sector2 = Math.round((100 * this.timeSector2) / this.timeTotal);
    const sector3 = 100 - sector1 - sector2;
    getGA().track({ action: 'dayTime', label: total, value: dayTime });
    getGA().track({ action: 'timeSection1', label: total, value: sector1 });
    getGA().track({ action: 'timeSection2', label: total, value: sector2 });
    getGA().track({ action: 'timeSection3', label: total, value: sector3 });
  }

  resetCameraTimer() {
    this.camera.stopTime = 0;
  }

  shutdown() {
    this.game.sounds.stopAudioSprite('loop', { spriteID: 'sfx_location' });
    this.location.onShowMacroView.remove(this.handleShowMacroView, this);

    this.gameHud.onToggleTime.remove(this.handleToggleTime, this);
    this.gameHud.onShowHint.remove(this.handleShowHint, this);
    this.gameHud.onScrollLeft.remove(this.handleScrollLeft, this);
    this.gameHud.onScrollRight.remove(this.handleScrollRight, this);

    this.camera.onPosition.remove(this.handleCameraOnPosition, this);

    this.container.destroy(true);
    this.container = null;
  }
}
